import React from 'react';

interface ImageToolbarProps {
  position: {
    blockId: string;
    x: number;
    y: number;
    popupX: number;
    popupY: number;
  };
  isVisible: boolean;
  onReplace?: () => void;
  onRemove?: () => void;
  onCrop?: () => void;
  onAlign?: (alignment: 'left' | 'center' | 'right') => void;
}

export const ImageToolbar: React.FC<ImageToolbarProps> = ({ 
  position, 
  isVisible, 
  onReplace, 
  onRemove, 
  onCrop, 
  onAlign 
}) => {
  if (!isVisible) {
    return null;
  }

  return (
    <div
      className="components-popover block-editor-block-popover"
      style={{
        position: 'fixed',
        top: position.popupY,
        left: position.popupX,
        transform: 'translateZ(0)',
        pointerEvents: 'auto',
        zIndex: 1000000,
        width: '300px',
        maxWidth: '90vw',
        boxShadow: '0 3px 30px rgba(25, 30, 35, 0.2)',
        borderRadius: '8px',
        border: '1px solid #ddd',
        backgroundColor: 'white',
        padding: '8px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif'
      }}
    >
      <div className="components-popover__content">
        <div 
          role="toolbar" 
          aria-orientation="horizontal" 
          aria-label="Image tools" 
          className="components-accessible-toolbar"
        >
          <div className="block-editor-block-toolbar">
            <div className="components-toolbar-group" style={{ display: 'flex', gap: '4px' }}>
              {/* Align Left */}
              <button 
                type="button"
                className="components-button has-icon"
                aria-label="Align Left"
                onClick={() => onAlign?.('left')}
                style={{
                  padding: '6px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16">
                  <path d="M3 3h18v2H3V3zm0 4h12v2H3V7zm0 4h18v2H3v-2zm0 4h12v2H3v-2z" />
                </svg>
              </button>
              
              {/* Align Center */}
              <button 
                type="button"
                className="components-button has-icon"
                aria-label="Align Center"
                onClick={() => onAlign?.('center')}
                style={{
                  padding: '6px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16">
                  <path d="M3 3h18v2H3V3zm3 4h12v2H6V7zm-3 4h18v2H3v-2zm3 4h12v2H6v-2z" />
                </svg>
              </button>
              
              {/* Align Right */}
              <button 
                type="button"
                className="components-button has-icon"
                aria-label="Align Right"
                onClick={() => onAlign?.('right')}
                style={{
                  padding: '6px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16">
                  <path d="M3 3h18v2H3V3zm6 4h12v2H9V7zm-6 4h18v2H3v-2zm6 4h12v2H9v-2z" />
                </svg>
              </button>
              
              {/* Crop */}
              <button 
                type="button"
                className="components-button has-icon"
                aria-label="Crop"
                onClick={onCrop}
                style={{
                  padding: '6px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16">
                  <path d="M17 15h2V7c0-1.1-.9-2-2-2H9v2h8v8zM7 17V1H5v4H1v2h4v10c0 1.1.9 2 2 2h10v4h2v-4h4v-2H7z" />
                </svg>
              </button>
              
              {/* Replace */}
              <button 
                type="button"
                className="components-button has-icon"
                aria-label="Replace"
                onClick={onReplace}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '500'
                }}
              >
                Replace
              </button>
              
              {/* Remove */}
              <button 
                type="button"
                className="components-button has-icon"
                aria-label="Remove"
                onClick={onRemove}
                style={{
                  padding: '6px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageToolbar;
